import { t } from '@lingui/macro';

import { removeUnderscores } from '@helpers/humanize';
import { successOptions } from '@helpers/sweet_alert_options';

const UPPY_COMPANION_SOURCE_MAP = {
  Box: "uppy-ucs-box",
  Dashboard: "uppy-ucs",
  Dropbox: "uppy-ucs-dropbox",
  GoogleDrive: "uppy-ucs-drive",
  Url: "uppy-ucs-url",
};

const formattedUppyAttachment = (file, fileSource = null) => {
  const source = BFG.hasFeature("uppy_with_companion_features")
    ? UPPY_COMPANION_SOURCE_MAP?.[fileSource] || "uppy-ucs"
    : "uppy";

  return {
    url: file.ssl_url,
    filename: file.original_name,
    mimetype: file.mime,
    size: file.size,
    height: file.meta.height,
    width: file.meta.width,
    folder_path: file.original_path,
    external_id: `${BFG.hasFeature("uppy_with_companion_features") ? "uppy" : "transloadit"}|${file.id}`,
    source,
  };
};

const uniqueFilesByKey = (files, key) => {
  const uniqFiles = {};
  files.forEach((file) => {
    uniqFiles[file[key]] = file;
  });
  return Object.values(uniqFiles);
};

const formattedUppyAssets = (files, activeLabelKey) => files.map((file) => ({
  attachments: [formattedUppyAttachment(file, file?.source)],
  ...activeLabelKey && { labels: [activeLabelKey] },
  name: removeUnderscores(file.original_basename),
}));

/**
 * @param {Record<string, Array>} groupedFiles - files grouped by basename,
 * same-name files become multiple attachments on one asset
 * @param {string|null} activeLabelKey
 * @returns {Array} asset data ready for the create assets API
 */
const formattedUppyAssetsGrouped = (groupedFiles, activeLabelKey) =>
  Object.entries(groupedFiles).map(([name, files]) => ({
    attachments: files.map((file) => formattedUppyAttachment(file, file?.source)),
    ...activeLabelKey && { labels: [activeLabelKey] },
    name: removeUnderscores(name),
  }));

/**
 * @deprecated
 */
const formattedUppyAttachments = (files) => files.map((file) => (
  formattedUppyAttachment(file, file?.source)
));

const duplicateSweetAlertOptions = {
  title: "Duplicate Files Detected",
  text: "Would you like to upload only one of each file?\n\n",
  type: "warning",
  showCancelButton: true,
  cancelButtonText: "No",
  confirmButtonText: "Yes",
  closeOnConfirm: true,
  customClass: "merging"
};

const TransloaditTemplateMap = () => {
  /* eslint-disable no-undef, key-spacing */
  if (BF_Environment === "development") {
    return {
      background_image:            "a0d72be0b3c84cfa8472a5b89efde945",
      card_image:                  "3206aae0349511e9869d6f52a2f3c8f4",
      email_logo:                  "6f59ac30349511e9be92adb72a190c5e",
      favicon_image:               "05638de0349611e9869d6f52a2f3c8f4",
      file_conversion:             "c35ae4b024e711e9b55f7b5484700320",
      file_conversion_watermark:   "94cd683024b911e9a8a7bd3965351113",
      file_ingest:                 "c1eed8f0200211e9862e4359eaf314a7",
      header_image:                "6670fc90348b11e9abf74174c765dd63",
      image_thumbnailer:           "8bea7f20203411e9a06077a28298ede6",
      image_watermark_thumbnailer: "5213c35020e411e990ff73ab14e458cc",
      logo_image:                  "2d29be80349611e9b4f4bfe8815a00e7",
      url_ingest:                  "0ea666001f5811e9bbd6430a93fce38e",
      url_screenshot_thumbnailer:  "007557b020f411e990ff73ab14e458cc",
      video_watermark:             "2cf293a53c46476b927a6a1cc2c8f8d8",
      watermark:                   "c1eed8f0200211e9862e4359eaf314a7"
    };
  }
  if (BF_Environment === "staging") {
    return {
      background_image:            "1a2f0a7139db4502975575c0b1d711bc",
      card_image:                  "320f9320361711e9a189a70e8c617ef5",
      email_logo:                  "22981ce0361811e98dd8055edf2e1ea9",
      favicon_image:               "4d5f3170361811e9909627f5887d15e6",
      file_conversion:             "b27ff200297611e98a5f3bbfdcb3df53",
      file_conversion_watermark:   "9f44b270297611e9b86377e66fb1900d",
      file_ingest:                 "8d164f00297611e992c7cb7c6782894b",
      header_image:                "f79e1c00361811e9824a2fe31b0d9d7d",
      image_thumbnailer:           "54219ec0297611e992c7cb7c6782894b",
      image_watermark_thumbnailer: "6aa85fd0297611e9b86377e66fb1900d",
      logo_image:                  "d9ce77a0361911e9943eb13d2c9e2d78",
      url_ingest:                  "384868a0297611e992c7cb7c6782894b",
      url_screenshot_thumbnailer:  "7d8fb300297611e9b86377e66fb1900d",
      video_watermark:             "5262dc5357ed412787e63eb86aee18f0",
      watermark:                   "8d164f00297611e992c7cb7c6782894b"
    };
  }
  return {
    background_image:            "3f5bbdabcb1340e0a225074d9f1b8828",
    card_image:                  "bd28fa70356111e9be950322b2fd1e7e",
    email_logo:                  "c51fbf10356211e99a35b7f34e23c325",
    favicon_image:               "a7277980356211e9869d6f52a2f3c8f4",
    file_conversion:             "2a635230297711e999523d7af4851778",
    file_conversion_watermark:   "1e8aa300297711e9b86377e66fb1900d",
    file_ingest:                 "11e41c80297711e9a14f1f411cdee99f",
    header_image:                "83c8c1b0356211e9b0e5d98333d44e1a",
    image_thumbnailer:           "ddcb51c0297611e9b72875093256a4ce",
    image_watermark_thumbnailer: "f1c881c0297611e9850f019573220028",
    logo_image:                  "dd6ac560356211e9b0e5d98333d44e1a",
    url_ingest:                  "d0050360297611e9a9d5e329af6c385f",
    url_screenshot_thumbnailer:  "03c91e70297711e9a14f1f411cdee99f",
    video_watermark:             "515f335ac7104403bfe38fcb932c5afe",
    watermark:                   "11e41c80297711e9a14f1f411cdee99f"
  };
  /* eslint-enable no-undef, key-spacing */
};

/**
 * Loads an image and returns its width and height.
 *
 * @param {Object} file - Object containing the image file as `data`.
 * @returns {Promise<{ width: number, height: number }>} Image dimensions.
 */
const loadImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = (err) => reject(err);
    img.src = URL.createObjectURL(file.data);
  });
};

/**
 * @function transformUppyUCSResult
 * @description transforms Uppy UCS upload result to the desired format
 * @param {Object} result - The result object from Uppy UCS upload
 * @returns {Array} Array of transformed file objects
 */
const transformUppyUCSResult = async (result) => {
  if (!result?.successful || !Array.isArray(result.successful)) {
    return [];
  }

  const transformedFiles = await Promise.all(
    result.successful.map(async (file) => {
      const extension = file.name.split('.').pop() || '';
      const basename = file.name.replace(`.${extension}`, '');
      const fileType = file.type?.split('/')[0] || 'unknown';

      // Initialize meta with existing data
      const meta = { ...(file.meta || {}) };

      // Add dimensions for image and video files
      if (fileType === 'image' || fileType === 'video') {
        try {
          const dimensions = await loadImage(file);
          meta.width = dimensions.width;
          meta.height = dimensions.height;
        } catch (error) {
          console.warn(`Failed to load image dimensions for ${file.name}`, error);
        }
      }

      return {
        id: file.id,
        name: file.name,
        basename: basename,
        ext: extension,
        size: file.size,
        mime: file.type,
        type: fileType,
        field: 'file',
        md5hash: file.md5hash || '',
        original_id: file.id,
        original_basename: basename,
        original_name: file.name,
        original_path: file?.data?.relativePath || '/',
        original_md5hash: file.md5hash || '',
        from_batch_import: false,
        is_tus_file: true,
        tus_upload_url: file.meta.brandfolderTusUrl || '',
        url: file.meta.brandfolderTusUrl || '',
        ssl_url: file.meta.brandfolderTusUrl || '',
        meta: meta,
        user_meta: file.user_meta || {},
        as: null,
        is_temp_url: false,
        cost: 0,
        queue: 'live',
        queue_time: 0,
        exec_time: 0,
        source: file.source,
      };
    })
  );

  return transformedFiles;
};

/**
 * @function isUppyUploaderEnabled
 * @description makes use of uppy specific feature flags to check if uppy uploader is enabled
 * @returns {boolean} whether uppy uploader is enabled or not
 */
const isUppyUploaderEnabled = () =>
  BFG.hasFeature('uppy_with_companion_features') ||
  BFG.hasFeature('uppy_uploader_features');

/**
 * @function getUniqueKey
 * @description gets the unique key for file deduplication based on feature flags
 * @returns {string} the unique key to use for file deduplication
 */
const getUniqueKey = () =>
  BFG.hasFeature('uppy_uploader_features') ? "md5hash" : "id";

/**
 * @function sanitizeUppyKey
 * @description sanitizes uppy key to remove /:*?"<>| characters which gets passed
 * @descriptionas a css selector to querySelectorAll in uppy internally
 * @param {string} key
 */
const sanitizeUppyKey = (key) => key.replace(/[\/\\:*?"<>|+]/g, '-');

const COMPANION_SANDBOX_BASE_URL = window.BFG.COMPANION_BASE_URL;
const COMPANION_SANDBOX_URL = `${COMPANION_SANDBOX_BASE_URL}/companion`;
const COMPANION_ALLOWED_HOSTS = [`${COMPANION_SANDBOX_BASE_URL}`];

/**
 * Parses a JWT token to extract expiration time.
 * @param {string} token - The JWT token to parse.
 * @returns {number|null} The expiration time in milliseconds since epoch, or null if parsing fails.
 */
const parseTokenExpiration = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid JWT token format');
      return null;
    }

    const payload = JSON.parse(atob(parts[1]));

    if (payload.exp) {
      // JWT exp is in seconds, convert to milliseconds
      return payload.exp * 1000;
    }

    return null;
  } catch (error) {
    console.error('Failed to parse token expiration:', error);
    return null;
  }
};

/**
 * Checks if a token is expired or about to expire.
 * @param {string} token - The JWT token to check.
 * @param {number} bufferMs - Buffer time in milliseconds before expiration (default: 5 minutes).
 * @returns {boolean} True if the token is expired or about to expire.
 */
const isTokenExpired = (token, bufferMs = 5 * 60 * 1000) => {
  const expirationTime = parseTokenExpiration(token);

  if (!expirationTime) {
    // falsy expiration time = expired token
    return true;
  }

  const now = Date.now();
  const timeUntilExpiration = expirationTime - now;

  return timeUntilExpiration <= bufferMs;
};

/**
 * Gets the identity token from the UCS SDK, checking for expiration first.
 * Returns an object indicating whether reinitialization is needed.
 * @param {UcsSdk} ucsSdk - UCS SDK instance.
 * @returns {Promise<{token: string, needsReinit: boolean}>} Token and reinitialization flag.
 */
const getRefreshedIdentityToken = async (ucsSdk) => {
  try {
    const currentToken = await ucsSdk.getIdentityToken();

    if (isTokenExpired(currentToken)) {
      return { token: currentToken, needsReinit: true };
    }

    return { token: currentToken, needsReinit: false };
  } catch (error) {
    console.error("Failed to get UCS identity token:", error);
    // If there's an error, signal that reinitialization might help
    return { token: null, needsReinit: true };
  }
};

/**
 * Gets a valid identity token, reinitializing the SDK if the token is expired.
 * @param {React.MutableRefObject} ucsSdkRef UCS SDK instance ref.
 * @param {Function} reinitializeSdk method to reinitialize ucs sdk.
 * @returns {Promise<string>} valid identity token.
 */
const getValidIdentityToken = async (ucsSdkRef, reinitializeSdk) => {
  if (!ucsSdkRef?.current) {
    throw new Error('UCS SDK not initialized');
  }

  const { token: identityToken, needsReinit } = await getRefreshedIdentityToken(ucsSdkRef.current);

  // If token is expired, reinitialize the SDK and get a fresh token
  if (needsReinit && reinitializeSdk) {
    console.log('Token expired, reinitializing SDK...');
    await reinitializeSdk();

    // Get fresh token from reinitialized SDK
    if (ucsSdkRef.current) {
      try {
        const freshToken = await ucsSdkRef.current.getIdentityToken();
        return freshToken;
      } catch (err) {
        console.error('Failed to get token after reinitialization:', err);
        // Fall back to the original token if reinitialization fails
        return identityToken;
      }
    }
  }

  return identityToken;
};

const UPLOAD_OVERHEAD_PER_FILE_MS = 8000;
const UPLOAD_BASELINE_BYTES_PER_MS = (125 * 1024) / 1000; // 125 KB/s = 1 Mb/s, converted to bytes/ms

const INVALID_FILENAMES_SET = new Set([
  // macOS
  '.DS_Store',
  '.AppleDouble',
  '.LSOverride',
  '.Spotlight-V100',
  '.Trashes',
  '.fseventsd',
  '.AppleDB',
  '.AppleDesktop',
  '.VolumeIcon.icns',
  // Windows
  'Thumbs.db',
  // Linux/KDE
  '.directory',
]);

/**
 * Handle uploading attachment versions for asset workflow check out/check in
 * @param options HandleUploadAssetAttachmentOptions
 * @returns void
 */
const handleUppyUploadAssetAttachment = ( options ) => {
  const { attachment, attachmentKey, files, onSuccess, setLoading } = options;

  setLoading(true);

  const file = formattedUppyAttachment(files[0]);

  $.ajax({
    url: `/api/v4/attachments/${attachmentKey}`,
    type: 'PUT',
    dataType: 'json',
    contentType: 'application/json',
    data: JSON.stringify({
      data: {
        attributes: {
          filename: file.filename || null,
          url: file.url || null,
          mimetype: file.mimetype || null,
          size: file.size ?? null,
          external_id: file.external_id || null,
          source: file.source || null,
          folder_path: file.folder_path || null,
        },
      },
    }),
    context: this,
    beforeSend(xhr) {
      xhr.setRequestHeader('Authorization', `Bearer ${BF_Token}`);
    },
    success: (response) => {
      window.swal(
        successOptions({
          customClass: 'attachment-updated',
          showCancelButton: false,
          showConfirmButton: true,
          successCopy: t`Your attachment has been updated!`,
        }),
        () => {}
      );

      const imageUrl = response.data.attributes.url;
      // update asset image on tab if updating the first attachment on asset modal Edit tab
      if (attachment.position === 0) {
        $('.j-asset-description img.m-asset-img-thumb').attr('src', imageUrl);
        // bulkRefreshSections([assetKey]);
      }

      onSuccess();
      setLoading(false);
    },
    error: (error) => {
      console.log('Error Updating Attachment', error);
      setLoading(false);
    },
  });
};

export {
  UPPY_COMPANION_SOURCE_MAP,
  formattedUppyAttachment,
  formattedUppyAttachments,
  formattedUppyAssets,
  formattedUppyAssetsGrouped,
  uniqueFilesByKey,
  duplicateSweetAlertOptions,
  TransloaditTemplateMap,
  isUppyUploaderEnabled,
  transformUppyUCSResult,
  getUniqueKey,
  sanitizeUppyKey,
  getRefreshedIdentityToken,
  getValidIdentityToken,
  parseTokenExpiration,
  isTokenExpired,
  handleUppyUploadAssetAttachment,
  COMPANION_SANDBOX_BASE_URL,
  COMPANION_SANDBOX_URL,
  COMPANION_ALLOWED_HOSTS,
  INVALID_FILENAMES_SET,
  UPLOAD_OVERHEAD_PER_FILE_MS,
  UPLOAD_BASELINE_BYTES_PER_MS,
};
